import React, { useState, useEffect, useCallback } from 'react';
import { twMerge } from 'utilities/src/twMerge';
import { type TFormField, TInputDropdownEventType } from 'ui/src/components/Form/shared/utils/types';
import { useRouter } from 'next/navigation';
import useGetSearchOptions from 'hooks/useGetSearchOptions';

import useCreateForm, { ValidationMode } from 'ui/src/components/Form';
import Button from 'components/Button';

import { validateForm } from 'utilities/src/validateSearch';
import { type IInputDropdownProps } from 'ui/src/components/Form/Input';

type SearchInputProps = {
  searchRef?: React.RefObject<HTMLInputElement>;
  handleKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  classes?: string;
  id: string;
  mode?: keyof ValidationMode;
  inputSize?: IInputDropdownProps<TFormField>['size'];
  buttonClasses?: string;
};

type TSearchFormFields = {
  search: {
    value: string;
    lastAction: TInputDropdownEventType | undefined;
  };
};

const submitActions = new Set<TInputDropdownEventType>(['Select', 'ImmediateSearch']);
const searchActions = new Set<TInputDropdownEventType>(['Search', 'ImmediateSearch']);

function SearchInput({
  searchRef,
  handleKeyDown,
  classes,
  id,
  mode = 'all',
  inputSize,
  buttonClasses,
}: SearchInputProps) {
  const [formFields, setFormFields] = useState<TSearchFormFields>({
    search: {
      value: '',
      lastAction: undefined,
    },
  });

  const { options } = useGetSearchOptions(formFields.search.value);

  const router = useRouter();

  /**
   * Set FormBuilder
   */

  const { FormBuilder, FormInput, formMethods } = useCreateForm<TSearchFormFields>({
    builderOptions: { mode },
  });

  /**
   * Search Submit Handler
   */

  const onSubmit = useCallback(() => {
    if (!formFields.search.lastAction) return;

    router.push(
      searchActions.has(formFields.search.lastAction)
        ? `/search?value=${formFields.search.value}`
        : formFields.search.value ?? '',
    );
  }, [formFields.search.value, formFields.search.lastAction, router]);

  /**
   * Select Lifecycle Handler
   */

  useEffect(() => {
    if (!formFields.search.lastAction) return;

    if (submitActions.has(formFields.search.lastAction)) {
      formMethods.handleSubmit(onSubmit)();
    }
  }, [formFields.search.lastAction, formMethods, onSubmit]);

  /**
   * Render
   */

  return (
    <FormBuilder onSubmit={onSubmit} className={twMerge('relative flex gap-1', classes)}>
      <FormInput.Dropdown
        id={id}
        searchRef={searchRef}
        onKeyDown={handleKeyDown}
        filterable={false}
        name="search"
        placeholder="Search for articles"
        labelPosition="Top"
        options={options}
        highlightOptions
        maxLength={120}
        onChangeText={(_searchText, _lastAction) => {
          setFormFields({ ...formFields, search: { value: _searchText, lastAction: _lastAction } });
        }}
        onChangeSelect={(_searchSelect, _lastAction) => {
          setFormFields({ ...formFields, search: { value: _searchSelect.value, lastAction: _lastAction } });
        }}
        required
        className="relative w-full"
        variant="Primary"
        size={inputSize}
        rules={validateForm.search}
      />

      <Button
        type="submit"
        variant="Primary"
        iconName="Search"
        variantColor="Black"
        tabIndex={0}
        classes={twMerge('gap-0', buttonClasses)}
      />
    </FormBuilder>
  );
}

export default SearchInput;
